import { ExternalLink, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CopyButton } from '@/components/ui/CopyButton';
import { useModelLookup } from '../../hooks/useModels';
import { useAuth } from '../../store/auth-context';
import { routes } from '../../lib/routes';
import type { Agent } from '../../types';
import type { Conversation, ConversationWithMessages } from '../../types';

interface ConversationDetailsPopoverProps {
  conversation: Conversation | ConversationWithMessages | undefined;
  agent: Agent | undefined;
}

const compactNumber = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function contextBarColor(percent: number, threshold: number): string {
  const criticalAt = Math.min(threshold * 1.1, 98);
  if (percent >= criticalAt || percent >= 95) return 'bg-red-500/60';
  if (percent >= threshold) return 'bg-orange-500/70';
  return 'bg-amber-500/40';
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right text-foreground">{value}</span>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  );
}

function IdRow({ label, id }: { label: string; id: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex min-w-0 items-center gap-1">
        <span
          className="select-all truncate font-mono text-[10px] text-muted-foreground"
          title={id}
        >
          {id}
        </span>
        <CopyButton value={id} title={`Copy ${label.toLowerCase()}`} />
      </div>
    </div>
  );
}

export function ConversationDetailsPopover({
  conversation,
  agent,
}: ConversationDetailsPopoverProps) {
  const modelLookup = useModelLookup();
  const { user } = useAuth();
  const agentSpaceId =
    (agent as (Agent & { spaceId?: string }) | undefined)?.spaceId ?? user?.defaultSpaceId ?? '';
  const stats =
    conversation && 'stats' in conversation ? conversation.stats : undefined;
  const ctx =
    conversation && 'lastContextUtilization' in conversation
      ? conversation.lastContextUtilization
      : undefined;
  const messages =
    conversation && 'messages' in conversation
      ? conversation.messages
      : undefined;

  const delegationCount = messages
    ? messages.reduce((n, m) => (m.delegationRef ? n + 1 : n), 0)
    : 0;

  const showLastActivity =
    conversation && conversation.updatedAt !== conversation.createdAt;

  // Match the live ContextUtilizationBar gate (src/components/assistant-ui/
  // context-utilization-bar.tsx): only worth surfacing when we're approaching
  // the compaction threshold. Below ~75% of threshold the value is noise
  // ("3 of 200k") and clutters the popover.
  const ctxShowAt = ctx ? (ctx.compactionThresholdPercent ?? 80) * 0.75 : 0;
  const showContext =
    !!ctx && ctx.contextLimit > 0 && ctx.utilizationPercent >= ctxShowAt;

  const modelDisplay = agent
    ? modelLookup[agent.modelId]?.displayName ?? agent.modelId
    : '';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Conversation details"
          aria-label="Conversation details"
        >
          <Info className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className="w-80 space-y-3 p-3 text-xs"
      >
        {!agent && !conversation ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : (
          <>
            {agent && (
              <div className="space-y-1.5">
                <SectionHeader>Agent</SectionHeader>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-foreground">
                    {agent.name}
                  </span>
                  <Link
                    to={routes.console.spaceAgent(agentSpaceId, agent.agentId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                    title="Open agent detail in new tab"
                    aria-label="Open agent detail in new tab"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </div>
                <div className="space-y-0.5">
                  <Row label="Model" value={modelDisplay} />
                </div>
              </div>
            )}

            {conversation && (
              <div className="space-y-1.5">
                <SectionHeader>Conversation</SectionHeader>
                <div className="space-y-0.5">
                  <Row
                    label="Created"
                    value={formatDateTime(conversation.createdAt)}
                  />
                  {showLastActivity && (
                    <Row
                      label="Last activity"
                      value={formatDateTime(conversation.updatedAt)}
                    />
                  )}
                  <Row label="Messages" value={conversation.messageCount} />
                </div>
              </div>
            )}

            {(stats || delegationCount > 0) && (
              <div className="space-y-1.5">
                <SectionHeader>Usage</SectionHeader>
                <div className="space-y-0.5">
                  {stats && <Row label="Runs" value={stats.totalRuns} />}
                  {stats && (
                    <Row label="Tool calls" value={stats.totalToolCalls} />
                  )}
                  {delegationCount > 0 && (
                    <Row label="Delegations" value={delegationCount} />
                  )}
                  {stats && (
                    <Row
                      label="Tokens"
                      value={
                        <span>
                          {compactNumber.format(stats.totalInputTokens)} in ·{' '}
                          {compactNumber.format(stats.totalOutputTokens)} out
                        </span>
                      }
                    />
                  )}
                </div>
              </div>
            )}

            {showContext && (
              <div className="space-y-1.5">
                <SectionHeader>Context</SectionHeader>
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Used</span>
                    <span className="text-right text-foreground">
                      {compactNumber.format(ctx!.inputTokens)} of{' '}
                      {compactNumber.format(ctx!.contextLimit)} (
                      {Math.round(ctx!.utilizationPercent)}%)
                    </span>
                  </div>
                  <div className="h-[3px] w-full overflow-hidden rounded-full bg-muted/30">
                    <div
                      className={`h-[3px] rounded-full ${contextBarColor(ctx!.utilizationPercent, ctx!.compactionThresholdPercent)}`}
                      style={{
                        width: `${Math.max(Math.min(ctx!.utilizationPercent, 100), 1)}%`,
                      }}
                    />
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Compacts at {ctx!.compactionThresholdPercent}%
                  </div>
                  {ctx!.compactionImminent && (
                    <div className="inline-flex items-center rounded border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 dark:text-orange-400">
                      Compaction imminent
                    </div>
                  )}
                </div>
              </div>
            )}

            {(conversation || agent) && (
              <div className="space-y-1.5 border-t border-border pt-2">
                {conversation && (
                  <IdRow
                    label="Conversation"
                    id={conversation.conversationId}
                  />
                )}
                {agent && <IdRow label="Agent" id={agent.agentId} />}
              </div>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
